import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { useTranslation } from '../hooks/useTranslation';
import { languageNames } from '../utils/i18n';
import { useMusic } from '../hooks/useMusic';
import { BuildingModal } from '../components/Base/BuildingModal';

/**
 * MenuPage Component - Updated Version
 * Using robust styles and custom logo as requested.
 */
export const MenuPage: React.FC = () => {
  const navigate = useNavigate();
  const resetGame = useGameStore(state => state.resetGame);
  const setTestMode = useGameStore(state => state.setTestMode);
  const language = useGameStore(state => state.language);
  const musicEnabled = useGameStore(state => state.musicEnabled);
  const musicVolume = useGameStore(state => state.musicVolume);
  const t = useTranslation();

  const [isLandscape, setIsLandscape] = React.useState(window.innerWidth > window.innerHeight);
  const [runeSequence, setRuneSequence] = React.useState<'waiting-left' | 'waiting-right' | 'idle'>('idle');
  const [runeTimeout, setRuneTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const [showNewGameModal, setShowNewGameModal] = React.useState(false);

  // Фоновая музыка для меню
  useMusic({ type: 'menu', volume: musicVolume, enabled: musicEnabled });

  React.useEffect(() => {
    const handleResize = () => setIsLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (runeTimeout) clearTimeout(runeTimeout);
    };
  }, [runeTimeout]);

  const handleNewGame = () => {
    setShowNewGameModal(true);
  };

  const confirmNewGame = () => {
    resetGame();
    setShowNewGameModal(false);
    navigate('/game-start');
  };

  const handleLeftBottomRuneClick = () => {
    if (runeSequence === 'idle') {
      setRuneSequence('waiting-right');
      // Сброс через 3 секунды, если не нажата правая руна
      const timeout = setTimeout(() => {
        setRuneSequence('idle');
      }, 3000);
      setRuneTimeout(timeout);
    }
  };

  const handleRightBottomRuneClick = () => {
    if (runeSequence === 'waiting-right') {
      // Последовательность выполнена - активируем тестовый режим
      if (runeTimeout) clearTimeout(runeTimeout);
      setTestMode();
      navigate('/game-start');
      setRuneSequence('idle');
    }
  };

  // Styles for the "Yellow Button" (matching WorldMap style)
  const menuButtonStyle: React.CSSProperties = {
    backgroundColor: '#ca8a04', // yellow-600
    color: '#000',
    padding: isLandscape ? '1rem 2rem' : '1.25rem 2.5rem',
    fontSize: isLandscape ? '0.8rem' : '1rem',
    fontWeight: 'bold',
    fontFamily: '"Press Start 2P", cursive, sans-serif',
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    boxShadow: '0 6px 0 #854d0e',
    display: 'block',
    width: '100%',
    maxWidth: isLandscape ? '280px' : '320px',
    margin: isLandscape ? '0' : '0 auto',
    transition: 'none'
  };

  const containerStyle: React.CSSProperties = {
    minHeight: 'calc(100vh - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0))',
    width: 'calc(100vw - env(safe-area-inset-left, 0) - env(safe-area-inset-right, 0))',
    paddingTop: 'env(safe-area-inset-top, 0)',
    paddingBottom: 'env(safe-area-inset-bottom, 0)',
    paddingLeft: 'env(safe-area-inset-left, 0)',
    paddingRight: 'env(safe-area-inset-right, 0)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000',
    backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000 100%)',
    position: 'relative',
    overflow: 'hidden',
    padding: isLandscape ? '2rem 8rem' : '2rem'
  };

  // Rune style
  const runeStyle: React.CSSProperties = {
    position: 'absolute',
    fontSize: isLandscape ? '4rem' : '3rem',
    color: '#ca8a04',
    textShadow: '0 0 10px rgba(202, 138, 4, 0.8), 0 0 20px rgba(202, 138, 4, 0.5), 0 0 30px rgba(202, 138, 4, 0.3)',
    fontFamily: 'serif',
    userSelect: 'none',
    transition: 'all 0.3s ease',
    zIndex: 5,
    opacity: 0.9,
    filter: 'drop-shadow(0 0 8px rgba(202, 138, 4, 0.6))'
  };

  const runeButtonStyle: React.CSSProperties = {
    ...runeStyle,
    cursor: 'pointer',
    opacity: 1
  };

  // Rune symbols (using Unicode runes)
  const runeSymbols = {
    topLeft: 'ᚠ',    // Fehu
    topRight: 'ᚢ',   // Uruz
    bottomLeft: 'ᚦ', // Thurisaz
    bottomRight: 'ᚨ'  // Ansuz
  };

  return (
    <div style={containerStyle}>
      {/* Background decoration */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.1, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', top: '10%', left: '10%', width: '40vw', height: '40vw', background: 'radial-gradient(circle, #ca8a04 0%, transparent 70%)', filter: 'blur(100px)' }} />
        <div style={{ position: 'absolute', bottom: '10%', right: '10%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, #854d0e 0%, transparent 70%)', filter: 'blur(120px)' }} />
      </div>

      {/* Corner Runes */}
      {/* Top Left Rune */}
      <div style={{ ...runeStyle, top: '2rem', left: '2rem' }}>
        {runeSymbols.topLeft}
      </div>

      {/* Top Right Rune */}
      <div style={{ ...runeStyle, top: '2rem', right: '2rem' }}>
        {runeSymbols.topRight}
      </div>

      {/* Bottom Left Rune (Clickable) */}
      <motion.div
        style={{
          ...runeButtonStyle,
          bottom: '2rem',
          left: '2rem',
          filter: runeSequence === 'waiting-right' ? 'brightness(1.3) drop-shadow(0 0 20px rgba(202, 138, 4, 0.9))' : 'drop-shadow(0 0 8px rgba(202, 138, 4, 0.6))'
        }}
        onClick={handleLeftBottomRuneClick}
        whileHover={runeSequence === 'idle' ? { scale: 1.15, filter: 'brightness(1.2) drop-shadow(0 0 15px rgba(202, 138, 4, 0.8))' } : {}}
        animate={runeSequence === 'waiting-right' ? {
          scale: [1, 1.1, 1],
          filter: ['brightness(1.3) drop-shadow(0 0 20px rgba(202, 138, 4, 0.9))', 'brightness(1.5) drop-shadow(0 0 25px rgba(202, 138, 4, 1))', 'brightness(1.3) drop-shadow(0 0 20px rgba(202, 138, 4, 0.9))']
        } : {}}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      >
        {runeSymbols.bottomLeft}
      </motion.div>

      {/* Bottom Right Rune (Clickable) */}
      <motion.div
        style={{
          ...runeButtonStyle,
          bottom: '2rem',
          right: '2rem',
          filter: runeSequence === 'waiting-right' ? 'brightness(1.5) drop-shadow(0 0 25px rgba(202, 138, 4, 1))' : 'drop-shadow(0 0 8px rgba(202, 138, 4, 0.6))'
        }}
        onClick={handleRightBottomRuneClick}
        whileHover={runeSequence === 'waiting-right' ? { scale: 1.15, filter: 'brightness(1.7) drop-shadow(0 0 30px rgba(202, 138, 4, 1))' } : {}}
        animate={runeSequence === 'waiting-right' ? {
          scale: [1, 1.1, 1],
          filter: ['brightness(1.5) drop-shadow(0 0 25px rgba(202, 138, 4, 1))', 'brightness(1.7) drop-shadow(0 0 30px rgba(202, 138, 4, 1))', 'brightness(1.5) drop-shadow(0 0 25px rgba(202, 138, 4, 1))']
        } : {}}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      >
        {runeSymbols.bottomRight}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        style={{
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          width: '100%',
          maxWidth: isLandscape ? '1000px' : '600px',
          display: 'flex',
          flexDirection: isLandscape ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isLandscape ? '4rem' : '0'
        }}
      >
        {/* Game Logo Image */}
        <div style={{
          marginBottom: isLandscape ? '0' : '4rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: isLandscape ? '1' : 'none'
        }}>
          <img
            src="sprites/menu/logo_game.png"
            alt="Heroes of Last Citadel Tactics"
            style={{
              width: '100%',
              maxWidth: isLandscape ? '450px' : '500px',
              height: 'auto',
              filter: 'drop-shadow(0 0 20px rgba(0,0,0,0.8))'
            }}
          />
        </div>

        {/* Menu Buttons Area */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isLandscape ? '1rem' : '2rem',
          width: '100%',
          flex: isLandscape ? '1' : 'none',
          alignItems: 'center'
        }}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/game-start')}
            style={menuButtonStyle}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(3px)';
              e.currentTarget.style.boxShadow = '0 3px 0 #854d0e';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 6px 0 #854d0e';
            }}
          >
            {t.menu.play}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNewGame}
            style={{ ...menuButtonStyle, backgroundColor: '#b91c1c', boxShadow: '0 6px 0 #7f1d1d' }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(3px)';
              e.currentTarget.style.boxShadow = '0 3px 0 #7f1d1d';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 6px 0 #7f1d1d';
            }}
          >
            {t.menu.newGame}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/settings')}
            style={{ ...menuButtonStyle, opacity: 0.9 }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(3px)';
              e.currentTarget.style.boxShadow = '0 3px 0 #854d0e';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 6px 0 #854d0e';
            }}
          >
            {t.menu.settings}
          </motion.button>

          {/* Language Indicator */}
          <div style={{
            marginTop: isLandscape ? '1rem' : '1.5rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#1a1a1a',
            border: '2px solid #ca8a04',
            borderRadius: '0.25rem',
            color: '#ca8a04',
            fontSize: isLandscape ? '0.6rem' : '0.7rem',
            fontFamily: '"Press Start 2P", cursive, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            width: '100%',
            maxWidth: isLandscape ? '280px' : '320px',
          }}
            onClick={() => navigate('/settings')}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#ca8a04';
              e.currentTarget.style.color = '#000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#1a1a1a';
              e.currentTarget.style.color = '#ca8a04';
            }}
          >
            🌐 {languageNames[language]}
          </div>
          <div style={{
            marginTop: '0.5rem',
            color: '#4b5563',
            fontSize: '0.6rem',
            fontFamily: 'monospace',
            textAlign: 'center',
            width: '100%',
          }}>
            v1.1 (Itch)
          </div>
        </div>
      </motion.div>

      {/* New Game Confirmation Modal */}
      {showNewGameModal && (
        <BuildingModal
          title={t.menu.newGame}
          onClose={() => setShowNewGameModal(false)}
          content={
            <div className="flex flex-col items-center gap-6">
              <p className="text-white text-center font-pixel text-sm mb-4">
                {t.menu.newGameConfirm}
              </p>
              <div className="flex gap-4 w-full">
                <button
                  onClick={confirmNewGame}
                  className="flex-1 py-3 bg-red-700 hover:bg-red-600 border-2 border-red-500 rounded text-white font-pixel text-xs uppercase transition-all shadow-[0_4px_0_0_rgba(127,29,29,1)] active:shadow-none active:translate-y-1"
                >
                  {t.common.yes}
                </button>
                <button
                  onClick={() => setShowNewGameModal(false)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 border-2 border-gray-500 rounded text-white font-pixel text-xs uppercase transition-all shadow-[0_4px_0_0_rgba(75,85,99,1)] active:shadow-none active:translate-y-1"
                >
                  {t.common.no}
                </button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};


